import type { Workspace } from '@up2stay/up2promo-api-client';
import {
  type SubscriptionStatus,
  WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { Button, Skeleton, cn } from '@up2stay/up2promo-ui';
import { Check, Lock, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

import { PLAN_PRICING, TRIAL_DAYS } from '@/core/billing/plan-catalog';
import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';
import { useFormatEur } from '@/core/billing/use-format-eur';
import {
  BillingCard,
  BillingChip,
} from '@/core/components/billing/billing-card';
import { BillingStatusBadge } from '@/core/components/billing/billing-status-badge';
import { useEntitlementLabels } from '@/core/components/billing/upgrade-surfaces';
import { ShellLink } from '@/core/components/navigation/shell-link';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

type BillingCurrentPlanCardProps = {
  plan: WorkspacePlan | undefined;
  status: SubscriptionStatus | undefined;
  workspace: Workspace | null;
  seats: number;
  isLoading?: boolean;
  isMobile: boolean;
  promoteCheckoutTrial: boolean;
  canManage: boolean;
  /** Paid-plan actions (portal / change) when not showing Free trial promo. */
  paidActions?: ReactNode;
  cycleLabel?: string;
};

export function BillingCurrentPlanCard({
  plan,
  status,
  workspace,
  seats,
  isLoading,
  isMobile,
  promoteCheckoutTrial,
  canManage,
  paidActions,
  cycleLabel,
}: BillingCurrentPlanCardProps) {
  const dict = useDictionary();
  const overview = dict.workspace.billing.overview;
  const { getPlanLabel } = useEntitlementLabels();
  const formatEur = useFormatEur();

  if (isLoading) {
    return (
      <BillingCard aria-label={overview.currentPlanLabel}>
        <Skeleton className="h-40 w-full rounded-xl" />
      </BillingCard>
    );
  }

  const isFree = plan === WorkspacePlan.FREE || plan == null;
  const planName = plan ? getPlanLabel(plan) : getPlanLabel(WorkspacePlan.FREE);
  const priceLabel = isFree
    ? formatEur(0)
    : plan === WorkspacePlan.PLUS
      ? formatEur(PLAN_PRICING[WorkspacePlan.PLUS].monthly)
      : plan === WorkspacePlan.TEAMS
        ? formatEur(PLAN_PRICING[WorkspacePlan.TEAMS].monthly)
        : null;

  const detail = isFree
    ? overview.freeDetail.replace('{seats}', String(Math.max(seats, 1)))
    : overview.paidDetailSeats
        .replace('{seats}', String(seats))
        .replace('{cycle}', cycleLabel ?? '');

  const showTrialPromo =
    promoteCheckoutTrial && canManage && canPurchaseBillingInApp();
  const days = String(TRIAL_DAYS);
  const plusMonthly = formatEur(PLAN_PRICING[WorkspacePlan.PLUS].monthly);

  return (
    <BillingCard aria-label={overview.currentPlanLabel}>
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            {overview.currentPlanLabel}
          </span>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[1.875rem] font-semibold leading-none tracking-[-0.02em] text-foreground">
              {planName}
            </span>
            {isFree ? (
              <BillingChip>{overview.freeForeverBadge}</BillingChip>
            ) : (
              <BillingStatusBadge
                plan={plan}
                status={status}
                workspace={workspace}
              />
            )}
          </div>
          <span className="text-sm text-muted-foreground">{detail}</span>
        </div>
        {priceLabel ? (
          <span className="shrink-0 text-[1.625rem] font-semibold tracking-[-0.02em] text-foreground">
            {priceLabel}
          </span>
        ) : null}
      </div>

      {isFree ? (
        <>
          <div
            className={cn(
              'grid gap-4 border-t border-border/70 pt-[18px]',
              isMobile ? 'grid-cols-1' : 'grid-cols-2 gap-6',
            )}
          >
            <FeatureList
              heading={overview.includedHeading}
              items={overview.included}
              tone="included"
            />
            <FeatureList
              heading={overview.unlockHeading}
              items={overview.unlock}
              tone="locked"
            />
          </div>

          {showTrialPromo ? (
            <div
              className={cn(
                'flex flex-col gap-3.5 rounded-[14px] border p-4',
                'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)]/70',
                'dark:border-primary/25 dark:bg-primary/10',
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-[10px]',
                    'bg-card text-primary shadow-sm',
                  )}
                >
                  <Sparkles className="size-[18px]" aria-hidden />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[15px] font-semibold text-foreground">
                    {overview.trialPromoTitle.replace('{days}', days)}
                  </span>
                  <span className="text-[13px] leading-snug text-muted-foreground">
                    {overview.trialPromoBody
                      .replace('{days}', days)
                      .replace('{price}', plusMonthly)}
                  </span>
                </div>
              </div>
              <div
                className={cn(
                  'flex gap-3',
                  isMobile ? 'flex-col' : 'flex-row items-center',
                )}
              >
                <Button variant="primary" className="h-11" asChild>
                  <ShellLink to={shellPaths.moreWorkspaceBillingUpgrade}>
                    {overview.startTrialCta}
                  </ShellLink>
                </Button>
                <Button variant="outline" className="h-11" asChild>
                  <ShellLink to={shellPaths.moreWorkspaceBillingUpgrade}>
                    {overview.comparePlansCta}
                  </ShellLink>
                </Button>
              </div>
            </div>
          ) : null}
        </>
      ) : paidActions ? (
        <div className="border-t border-border/70 pt-4">{paidActions}</div>
      ) : null}
    </BillingCard>
  );
}

function FeatureList({
  heading,
  items,
  tone,
}: {
  heading: string;
  items: string[];
  tone: 'included' | 'locked';
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <span className="text-[13px] font-semibold text-foreground">
        {heading}
      </span>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {items.map((item) => (
          <li
            key={item}
            className={cn(
              'flex gap-2.5 text-sm leading-snug',
              tone === 'locked' ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {tone === 'included' ? (
              <Check
                className="mt-0.5 size-4 shrink-0 text-emerald-700 dark:text-emerald-400"
                aria-hidden
              />
            ) : (
              <Lock
                className="mt-0.5 size-[15px] shrink-0 text-muted-foreground"
                aria-hidden
              />
            )}
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
